import { FieldPath, FieldValues } from 'react-hook-form';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faRightLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Form from 'components/form';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { Household } from 'types/domain';

interface UseFormRelationshipPickerProps<T extends FieldValues, K extends FieldPath<T>> {
    contact: Household['contacts'][0];
    relationToPath: K;
    relationFromPath: K;
    required?: boolean;
}
const UseFormRelationshipPicker = <T extends FieldValues, K extends FieldPath<T>>(
    props: UseFormRelationshipPickerProps<T, K>,
) => {
    const { contact: selectedContact, relationToPath, relationFromPath, required = false } = props;
    const { data: contact } = useCurrentContact();
    const { path, control, watch } = Form.useGivebutterFormContext<T>();
    const [relationTo, relationFrom] = watch([relationToPath, relationFromPath]);

    const isToRequired = !!relationFrom || required;
    const isFromRequired = !!relationTo || required;

    return (
        <div className={'flex flex-col gap-4'}>
            <p className={'text-base font-medium'}>
                {selectedContact?.first_name} {selectedContact?.last_name}
            </p>
            <div className={'flex items-center gap-2'}>
                <Form.Container>
                    <Form.Label name={path(relationToPath)}>
                        {contact.first_name} is {selectedContact?.first_name}{' '}
                        {selectedContact?.last_name}'s
                    </Form.Label>
                    <Form.SingleSelect
                        control={control}
                        name={relationToPath}
                        rules={{ required: isToRequired && 'Please select a relationship' }}
                    >
                        <SingleSelect.Trigger aria-label="Select relationship" />
                        <SingleSelect.Content>
                            <RelationshipListItems />
                        </SingleSelect.Content>
                    </Form.SingleSelect>
                    <Form.Error name={path(relationToPath)} />
                </Form.Container>
                <FontAwesomeIcon className={'mt-5 text-gray-500'} icon={faRightLeft} />
                <Form.Container>
                    <Form.Label name={path(relationFromPath)}>
                        {selectedContact?.first_name} is {contact.first_name} {contact.last_name}
                        's
                    </Form.Label>
                    <Form.SingleSelect
                        control={control}
                        name={relationFromPath}
                        rules={{ required: isFromRequired && 'Please select a relationship' }}
                    >
                        <SingleSelect.Trigger aria-label="Select relationship" />
                        <SingleSelect.Content>
                            <RelationshipListItems />
                        </SingleSelect.Content>
                    </Form.SingleSelect>
                    <Form.Error name={path(relationFromPath)} />
                </Form.Container>
            </div>
        </div>
    );
};

const RelationshipListItems = () => {
    return (
        <>
            <SingleSelect.Item value="aunt">
                <SingleSelect.ItemText>Aunt</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="brother">
                <SingleSelect.ItemText>Brother</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="brother-in-law">
                <SingleSelect.ItemText>Brother-in-law</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="co-worker">
                <SingleSelect.ItemText>Co-worker</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="child">
                <SingleSelect.ItemText>Child</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="cousin">
                <SingleSelect.ItemText>Cousin</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="daughter">
                <SingleSelect.ItemText>Daughter</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="daughter-in-law">
                <SingleSelect.ItemText>Daughter-in-law</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="employee">
                <SingleSelect.ItemText>Employee</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="employer">
                <SingleSelect.ItemText>Employer</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="father">
                <SingleSelect.ItemText>Father</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="father-in-law">
                <SingleSelect.ItemText>Father-in-law</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="friend">
                <SingleSelect.ItemText>Friend</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="granddaughter">
                <SingleSelect.ItemText>Granddaughter</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="grandfather">
                <SingleSelect.ItemText>Grandfather</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="grandmother">
                <SingleSelect.ItemText>Grandmother</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="grandson">
                <SingleSelect.ItemText>Grandson</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="husband">
                <SingleSelect.ItemText>Husband</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="mother">
                <SingleSelect.ItemText>Mother</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="mother-in-law">
                <SingleSelect.ItemText>Mother-in-law</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="nephew">
                <SingleSelect.ItemText>Nephew</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="niece">
                <SingleSelect.ItemText>Niece</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="parent">
                <SingleSelect.ItemText>Parent</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="partner">
                <SingleSelect.ItemText>Partner</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="roommate">
                <SingleSelect.ItemText>Roommate</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="sister">
                <SingleSelect.ItemText>Sister</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="sister-in-law">
                <SingleSelect.ItemText>Sister-in-law</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="sibling">
                <SingleSelect.ItemText>Sibling</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="son">
                <SingleSelect.ItemText>Son</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="son-in-law">
                <SingleSelect.ItemText>Son-in-law</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="spouse">
                <SingleSelect.ItemText>Spouse</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="step-brother">
                <SingleSelect.ItemText>Step-brother</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="step-daughter">
                <SingleSelect.ItemText>Step-daughter</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="step-father">
                <SingleSelect.ItemText>Step-father</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="step-mother">
                <SingleSelect.ItemText>Step-mother</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="step-sister">
                <SingleSelect.ItemText>Step-sister</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="step-son">
                <SingleSelect.ItemText>Step-son</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="uncle">
                <SingleSelect.ItemText>Uncle</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="wife">
                <SingleSelect.ItemText>Wife</SingleSelect.ItemText>
            </SingleSelect.Item>
            <SingleSelect.Item value="other">
                <SingleSelect.ItemText>Other</SingleSelect.ItemText>
            </SingleSelect.Item>
        </>
    );
};

export { UseFormRelationshipPicker };
export type { UseFormRelationshipPickerProps };
